import { useEffect, useState } from 'react';

import { HeatPumpIndoorUnit, HeatPumpOutdoorUnit, Residence, SelectedOfferProducts } from '~src/model';
import {
  calculateHeatPumpScores,
  calculateResidenceHeatingRequired,
  findBestCompatibleIndoorUnit,
  isResidenceFilled,
} from '~src/util';

type CalculatorProductsProps = {
  residence?: Residence;
  outdoorUnits: HeatPumpOutdoorUnit[];
  indoorUnits: HeatPumpIndoorUnit[];
};

export const useCalculatorProducts = ({ residence, outdoorUnits, indoorUnits }: CalculatorProductsProps) => {
  const [heatPumpProducts, setHeatPumpProducts] = useState<SelectedOfferProducts>({});
  const [offerProducts, setOfferProducts] = useState<SelectedOfferProducts>({});
  const [manuallySet, setManuallySet] = useState(false);

  const updateOfferProducts = (products: SelectedOfferProducts) => {
    setManuallySet(true);
    setOfferProducts(products);
  };

  useEffect(
    function setDefaultHeatPumpProducts() {
      if (manuallySet || !residence || !isResidenceFilled(residence) || outdoorUnits.length === 0) {
        setHeatPumpProducts({ heatPumpOutdoorUnit: undefined, heatPumpIndoorUnit: undefined });
        return;
      }

      const heatingRequired = calculateResidenceHeatingRequired(residence);
      const { available, bestUnavailable } = calculateHeatPumpScores(
        outdoorUnits,
        { ...residence, noiseWeight: 'small' },
        heatingRequired
      );

      const [outdoorUnit] = available ?? [bestUnavailable];
      const indoorUnit = findBestCompatibleIndoorUnit(outdoorUnit, indoorUnits) ?? indoorUnits[0];

      setHeatPumpProducts({
        heatPumpOutdoorUnit: outdoorUnit,
        heatPumpIndoorUnit: indoorUnit,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [residence, outdoorUnits.length, indoorUnits.length]
  );

  useEffect(
    function setProducts() {
      if (manuallySet) {
        return;
      }

      setOfferProducts(heatPumpProducts);
    },
    [manuallySet, heatPumpProducts]
  );

  return {
    offerProducts,
    setOfferProducts: updateOfferProducts,
  };
};
